import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Users, BarChart3, Send, FolderOpen, Bell, Briefcase } from "lucide-react";

const heroImg = "https://images.unsplash.com/photo-1573497620053-ea5300f94f21?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function ForAgencies() {
  return (
    <div data-testid="for-agencies-page">
      <section className="border-b border-[#D1D5DB] bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-4">For Case Managers & Agencies</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold leading-[1.05]">
              Stop calling. <br /> <span className="text-[#C05621]">Start placing.</span>
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-xl">
              Built for VA contractors, reentry agencies, recovery programs, and social service teams. One dashboard, the whole roster, real placement outcomes.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register?role=case_manager" data-testid="cm-register"><Button className="bg-[#C05621] hover:bg-[#9C4221] text-white px-7 py-6">Open Pro Account <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              <Link to="/pricing"><Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#0A192F] px-7 py-6">See Plans</Button></Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img src={heroImg} alt="Case manager" className="w-full h-[420px] object-cover rounded-sm" />
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-[#D1D5DB]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20">
          <div className="max-w-2xl">
            <div className="shv-rule mb-4"></div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">A real placement workflow — not another spreadsheet.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <Feature icon={<FolderOpen />} title="Save client profiles" body="Build a private roster. Re-use intake details across referrals." />
            <Feature icon={<Send />} title="Submit referrals" body="One-click referrals with notes, eligibility, and Verified Applicant badges." />
            <Feature icon={<BarChart3 />} title="Track referral status" body="From Submitted → Under Review → Accepted → Placed → Completed. Every event logged." />
            <Feature icon={<Bell />} title="Real-time vacancy alerts" body="Push notifications and email the moment a matching bed opens." />
            <Feature icon={<Users />} title="Agency team accounts" body="Multi-user logins, role-based access, agency-wide reporting." />
            <Feature icon={<Briefcase />} title="Enterprise contracts" body="Annual contracts available for VA contractors, government agencies, and large nonprofits." />
          </div>
        </div>
      </section>

      <section className="bg-[#F5F5F0] border-b border-[#D1D5DB]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">Two plans. Both built for outcomes.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            <div className="bg-white border border-[#D1D5DB] p-8 rounded-sm text-left">
              <div className="font-heading font-semibold text-[#0A192F]">Professional</div>
              <div className="font-heading text-4xl font-bold text-[#0A192F] mt-2">$19.99<span className="text-base font-normal text-[#4A5568]">/mo</span></div>
              <div className="text-sm text-[#4A5568] mt-2">Independent case managers. Full pro dashboard.</div>
            </div>
            <div className="bg-white border-2 border-[#1E3A8A] p-8 rounded-sm text-left relative">
              <div className="absolute -top-3 left-7 bg-[#1E3A8A] text-white text-[10px] tracking-widest uppercase font-bold px-3 py-1">Agency</div>
              <div className="font-heading font-semibold text-[#0A192F]">Agency Team</div>
              <div className="font-heading text-4xl font-bold text-[#0A192F] mt-2">$99<span className="text-base font-normal text-[#4A5568]">/mo</span></div>
              <div className="text-sm text-[#4A5568] mt-2">Unlimited team members, agency-wide reporting, priority support.</div>
            </div>
          </div>
          <Link to="/pricing" className="inline-block mt-10"><Button className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white px-7 py-6">See Full Pricing <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
        </div>
      </section>
    </div>
  );
}

const Feature = ({ icon, title, body }) => (
  <div>
    <div className="w-10 h-10 rounded-sm bg-[#F5F5F0] border border-[#D1D5DB] flex items-center justify-center text-[#1E3A8A] mb-4">
      {React.cloneElement(icon, { className: "w-5 h-5" })}
    </div>
    <div className="font-heading font-semibold text-[#0A192F] text-lg">{title}</div>
    <div className="text-sm text-[#4A5568] mt-1.5">{body}</div>
  </div>
);
